import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

const ASPECT = 16 / 9;
const MAX_OUTPUT_WIDTH = 1600;

interface CoverCropDialogProps {
  /** The picked static image awaiting crop; null keeps the dialog closed. */
  file: File | null;
  onCancel: () => void;
  onCropped: (file: File, previewUrl: string) => void;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
    img.src = src;
  });
}

/** Draw the selected 16:9 area onto a canvas and export it as a File. */
async function cropAreaToFile(src: string, area: Area, sourceFile: File): Promise<{ file: File; url: string }> {
  const img = await loadImage(src);
  const outW = Math.min(Math.round(area.width), MAX_OUTPUT_WIDTH);
  const outH = Math.round(outW / ASPECT);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, outW, outH);

  // PNG stays PNG (crisp for screenshots/text), everything else exports as JPEG.
  const mime = sourceFile.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Zuschnitt fehlgeschlagen'))), mime, 0.92);
  });
  const ext = mime === 'image/png' ? 'png' : 'jpg';
  const baseName = sourceFile.name.replace(/\.[^.]+$/, '');
  const file = new File([blob], `${baseName}-cover.${ext}`, { type: mime });
  return { file, url: URL.createObjectURL(file) };
}

export const CoverCropDialog: React.FC<CoverCropDialogProps> = ({ file, onCancel, onCropped }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh object URL + reset controls whenever a new file comes in.
  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
    setError(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !areaPixels || !file) return;
    setProcessing(true);
    setError(null);
    try {
      const { file: croppedFile, url } = await cropAreaToFile(imageSrc, areaPixels, file);
      onCropped(croppedFile, url);
    } catch (err) {
      console.error('[Updates] crop', err);
      setError(err instanceof Error ? err.message : 'Zuschnitt fehlgeschlagen.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Cover zuschneiden</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-[320px] rounded-lg overflow-hidden bg-black/90">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-sm text-muted-foreground shrink-0">Zoom</Label>
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.01}
            onValueChange={([v]) => setZoom(v)}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Ziehe das Bild zurecht und zoome, bis der Ausschnitt passt. Das Cover wird immer im 16:9-Format angezeigt.
        </p>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={processing}>Abbrechen</Button>
          <Button onClick={handleConfirm} disabled={processing || !areaPixels}>
            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Zuschneiden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
